/**
 * Composition check for the Witness Composition Theorem (T3).
 *
 * Two PreservationWitnesses χ₁ : E₀ → E₁ and χ₂ : E₁ → E₂ compose into
 * a chain witness iff:
 *
 *   - they target the same invariant_id and kind;
 *   - the fingerprints are continuous: χ₁.post_fingerprint = χ₂.pre_fingerprint;
 *   - neither carries drift severity ∈ {high, unknown};
 *   - both individually type as preservation witnesses (delegated to
 *     `validatePreservationWitness`).
 *
 * On refusal the chain emits exactly one primary rulebook code naming the
 * structural break.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import {
  validatePreservationWitness,
  type IssuerCapabilityInput,
  type PreservationWitnessInput,
  type RefuseVerdict,
  type Verdict,
} from "./validatePreservation.js";

export interface ComposeAcceptVerdict {
  readonly outcome: "accept";
  readonly chainSubject: string;
  readonly chainFingerprintHead: string;
  readonly chainFingerprintTail: string;
}

export interface ComposeChainRefuseVerdict extends RefuseVerdict {
  readonly outcome: "refuse";
  readonly stage: "chi-1" | "chi-2" | "composition";
}

export type ComposeVerdict = ComposeAcceptVerdict | ComposeChainRefuseVerdict;

export function composeWitnesses(
  chi1: PreservationWitnessInput,
  chi2: PreservationWitnessInput,
  icap: IssuerCapabilityInput,
): ComposeVerdict {
  const subject = `χ₂∘χ₁:${chi1.invariant_id}`;

  const v1 = validatePreservationWitness(chi1, icap);
  if (v1.outcome === "refuse") {
    return { ...v1, stage: "chi-1" };
  }

  const v2 = validatePreservationWitness(chi2, icap);
  if (v2.outcome === "refuse") {
    return { ...v2, stage: "chi-2" };
  }

  if (chi1.invariant_id !== chi2.invariant_id) {
    return chainRefuse(
      subject,
      "ILG-P-002",
      "Fingerprint continuity break (invariant id mismatch)",
      "critical",
      `χ₁ targets ${chi1.invariant_id}; χ₂ targets ${chi2.invariant_id}. The composition is not over a single invariant.`,
    );
  }
  if (chi1.kind !== chi2.kind) {
    return chainRefuse(
      subject,
      "ILG-DR-002",
      "Kind change without re-establishment",
      "critical",
      `χ₁.kind = ${chi1.kind}; χ₂.kind = ${chi2.kind}. A kind change requires fresh establishment lineage, not chain composition.`,
    );
  }

  const head = chi1.pre_fingerprint ?? `fp:E0:${chi1.invariant_id}`;
  const tail = chi2.post_fingerprint ?? `fp:E2:${chi2.invariant_id}`;
  const join1 = chi1.post_fingerprint ?? `fp:E1:${chi1.invariant_id}`;
  const join2 = chi2.pre_fingerprint ?? `fp:E1:${chi2.invariant_id}`;

  if (join1 !== join2) {
    return chainRefuse(
      subject,
      "ILG-P-002",
      "Fingerprint continuity break (chain join)",
      "critical",
      `χ₁.post_fingerprint = ${join1}; χ₂.pre_fingerprint = ${join2}. The chain join is structurally broken — the chain witness is uninhabited.`,
    );
  }

  return {
    outcome: "accept",
    chainSubject: subject,
    chainFingerprintHead: head,
    chainFingerprintTail: tail,
  };
}

function chainRefuse(
  subject: string,
  rule: string,
  title: string,
  severity: RefuseVerdict["severity"],
  explanation: string,
): ComposeChainRefuseVerdict {
  return {
    outcome: "refuse",
    subject,
    rule,
    title,
    severity,
    explanation,
    stage: "composition",
  };
}

// `Verdict` is re-exported for callers that want to handle the entire
// validator surface in one switch without importing both modules.
export type { Verdict };
